import {
  Award,
  Banknote,
  Bot,
  Camera,
  ClipboardCheck,
  Gauge,
  Gift,
  LayoutDashboard,
  MapPinned,
  MessageCircle,
  Radar,
  Repeat,
  Scissors,
  ShieldCheck,
  ShoppingBasket,
  Thermometer,
  Truck,
  Trophy,
  type LucideIcon,
} from "lucide-react";

export type ProductId = "customer-app" | "driver-app" | "supplier-portal" | "admin-command-centre";

export interface ProductHighlightMedia {
  type: "image" | "video";
  src: string;
  poster?: string;
  captionsSrc?: string;
  alt: string;
  credit?: string;
}

export interface ProductHighlight {
  icon: LucideIcon;
  title: string;
  description: string;
  expandedDetail: string;
  /** Left undefined on every real entry — see products.ts's module comment. */
  media?: ProductHighlightMedia;
}

export interface ProductDemoVideo {
  src: string;
  posterSrc?: string;
  captionsSrc: string;
}

export interface Product {
  id: ProductId;
  name: string;
  icon: LucideIcon;
  summary: string;
  internalOnly: boolean;
  audienceNote?: string;
  highlights: ProductHighlight[];
  /** Left undefined — no real ZenFresh product demo footage exists yet. */
  demoVideo?: ProductDemoVideo;
  ctaLabel?: string;
  ctaHref?: string;
}

// TODO(content): highlight `media` (screenshots/demo clips) is deliberately
// omitted on every entry below — there are no real ZenFresh Customer/Driver
// App screenshots or an Admin Command Centre to (sanitised-)screenshot yet.
// Showing stock/mockup imagery and presenting it as "our app" would be a
// specific, checkable false claim, the same class of issue this project
// already avoided for ZP-10's founder photo and ZP-99's facility gallery.
// ProductHighlightCarousel.tsx renders a text-only expand while media is
// undefined; add a real `media` object here once real assets exist.
export const PRODUCTS: Product[] = [
  {
    id: "customer-app",
    name: "Customer App",
    icon: ShoppingBasket,
    summary: "Order traceable, sustainably-caught seafood for same-day delivery.",
    internalOnly: false,
    highlights: [
      {
        icon: Gauge,
        title: "Freshness Scoring",
        description: "Every listing shows a freshness score based on catch-to-delivery time.",
        expandedDetail:
          "Each item is scored from dock to doorstep — the shorter the gap between catch and delivery, the higher the score, so you can compare freshness at a glance before you buy.",
      },
      {
        icon: Scissors,
        title: "Cleaning & Yield Options",
        description: "Choose whole, cleaned, or filleted — with an estimated usable yield.",
        expandedDetail:
          "Pick how your seafood arrives — scaled, gutted, filleted — and see an estimated usable weight upfront, so there are no surprises when you unbox it.",
      },
      {
        icon: Bot,
        title: "AI Recommendations",
        description: "Personalized picks based on what's fresh, in season, and to your taste.",
        expandedDetail:
          "Recommendations blend what's freshest today, what's in season along the coast, and your own order history — not just what's trending.",
      },
      {
        icon: Repeat,
        title: "Subscriptions",
        description: "Set a weekly or biweekly seafood box and skip the reordering.",
        expandedDetail:
          "Build a recurring order once — weekly or biweekly — and adjust or skip any cycle from the app. No calls, no re-browsing the menu each time.",
      },
      {
        icon: Award,
        title: "ZenPass Membership",
        description: "Free delivery, early access to limited catch, and member pricing.",
        expandedDetail:
          "ZenPass members get waived delivery fees, first access to limited seasonal catch, and preferential pricing on premium items.",
      },
    ],
  },
  {
    id: "driver-app",
    name: "Driver App",
    icon: Truck,
    summary: "The cold-chain and delivery toolkit for ZenFresh's delivery partners.",
    internalOnly: false,
    highlights: [
      {
        icon: Thermometer,
        title: "Cold-Chain Temperature Monitoring",
        description: "Real-time temperature logging for every insulated delivery box.",
        expandedDetail:
          "Each delivery box is logged at pickup and checked again on delivery — if a shipment drifts out of safe range, it's flagged before it reaches a customer.",
      },
      {
        icon: MapPinned,
        title: "Live GPS Tracking",
        description: "Turn-by-turn routing with live ETA shared with the customer.",
        expandedDetail:
          "Drivers get optimized turn-by-turn routing for cold-chain delivery windows, and customers see a live ETA rather than a vague delivery day.",
      },
      {
        icon: ClipboardCheck,
        title: "Delivery Closure & Proof-of-Delivery",
        description: "Photo or signature confirmation closes out every delivery.",
        expandedDetail:
          "A quick photo or signature at the doorstep closes the delivery loop, giving both the driver and the customer a clear record of a completed handoff.",
      },
      {
        icon: Trophy,
        title: "Earnings & Gamification",
        description: "Transparent per-delivery earnings, streak bonuses, and weekly leaderboards.",
        expandedDetail:
          "Drivers see exactly what each delivery pays before accepting it, plus streak bonuses for consistency and a weekly leaderboard for top performers.",
      },
    ],
  },
  {
    id: "supplier-portal",
    name: "Supplier & Procurement Portal",
    icon: Radar,
    summary: "How ZenFresh works with the fishing families and crews who supply the catch.",
    internalOnly: false,
    highlights: [
      {
        icon: ClipboardCheck,
        title: "Catch Declaration",
        description: "Fishermen log their catch — species, weight, catch location — right from the dock.",
        expandedDetail:
          "A simple on-dock flow lets a fishing crew declare what they've caught, how much, and where, which becomes the traceability record a customer later sees.",
      },
      {
        icon: Banknote,
        title: "Fair-Rate Purchase Orders",
        description: "Prices are set and visible before the catch changes hands — no haggling.",
        expandedDetail:
          "Purchase orders reflect a published fair rate agreed before the sale — fishermen know exactly what they'll be paid before they hand over the catch.",
      },
      {
        icon: MessageCircle,
        title: "Vernacular / WhatsApp-Based Interaction",
        description: "Works over WhatsApp in regional languages — no app literacy required.",
        expandedDetail:
          "Not every partner crew wants to learn a new app — the portal also works through WhatsApp messages in regional languages, so anyone can participate.",
      },
      {
        icon: ShieldCheck,
        title: "Supplier Scorecards",
        description: "Consistency and quality tracked over time, feeding back into fair pricing.",
        expandedDetail:
          "Every supplier builds a track record on consistency, quality, and reliability — a strong scorecard translates into better long-term terms.",
      },
      {
        icon: Banknote,
        title: "Timely Settlement",
        description: "Payment on a fixed schedule — not whenever a middleman gets around to it.",
        expandedDetail:
          "Settlement happens on a predictable, fixed schedule, replacing the informal and often-delayed payment terms of a traditional middleman chain.",
      },
    ],
    ctaLabel: "Become a Supplier",
    ctaHref: "/partnerships",
  },
  {
    id: "admin-command-centre",
    name: "Admin Command Centre",
    icon: LayoutDashboard,
    summary: "The internal operations dashboard that keeps the whole network running.",
    internalOnly: true,
    audienceNote: "For ZenFresh Operations Team",
    highlights: [
      {
        icon: Gauge,
        title: "Real-Time KPI Dashboard",
        description: "Order volume, delivery SLAs, and cold-chain compliance in one view.",
        expandedDetail:
          "Operations leads see live order volume, on-time delivery rates, and cold-chain compliance across every hub, without digging through separate reports.",
      },
      {
        icon: Camera,
        title: "Order, Fleet & Zone Management",
        description: "One console to manage active orders, the driver fleet, and delivery zones.",
        expandedDetail:
          "Order routing, driver fleet status, and delivery-zone configuration are managed from a single console rather than disconnected tools.",
      },
      {
        icon: Bot,
        title: "AI-Driven Insights",
        description: "Demand forecasting and anomaly detection surface issues before they escalate.",
        expandedDetail:
          "Forecasting models flag demand spikes ahead of time, and anomaly detection surfaces cold-chain or delivery issues early enough to act on them.",
      },
      {
        icon: Gift,
        title: "Investor & Partner Narrative",
        description: "The operational depth behind the brand — see Press & Investor Relations.",
        expandedDetail:
          "This tooling is part of what we show investors and partners evaluating ZenFresh's operational maturity beyond the consumer-facing app.",
      },
    ],
    ctaLabel: "See Press & Investor Relations",
    ctaHref: "/press-investors",
  },
];
